import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Input, Button, Switch, Select, DatePicker, Slider } from 'antd';
import ModalDlg from 'components/Basic/ModalDlg';
import { ArrowForward, ArrowDropDown, Add, Delete, Settings, EmojiEvents, Close } from '@material-ui/icons';
import toast from 'components/Basic/Toast';
import { useAppDispatch, useAppSelector } from 'utilities/redux';
import {
  useCreateSetupMutation,
  useUpdateSetupMutation,
} from 'core/worldCup/setup/SetupService';
import { Setup } from 'core/worldCup/setup/types';
import { useLazyGetPrizesSimpleListQuery } from 'core/worldCup/prizes/PrizesService';
import { useLazyGetPrizeTypesSimpleListQuery } from 'core/worldCup/prizeTypes/PrizeTypesService';
import { setPrizeTypesData } from 'core/worldCup/prizeTypes/PrizeTypesSlice';
import { sanitizeRule } from 'utilities/sanitize';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getApiErrorMessage } from 'utilities/getApiErrorMessages';
dayjs.extend(utc);

type SetupFormData = {
  name: string;
  startAt: any;
  endAt: any;
  isActive: boolean;
  isPublished: boolean;
};

type PrizeRow = {
  rewardTypeId: number | undefined;
  rewardId: number | undefined;
  quantity: number | undefined;
  weight: number | undefined;
  redeemChannel: string | undefined;
};

const REDEEM_CHANNELS = [
  { value: 'APP', label: 'APP' },
];

type SetupFormModalProps = {
  mode: 'modal' | 'fullscreen';
  setup: Setup | null;
  isOpenModal?: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  campaignId?: number | null;
};

const DEFAULT_PRIZE_ROW = (prizeTypes: any[]): PrizeRow => ({
  rewardTypeId: prizeTypes.length === 1 ? prizeTypes[0].id : undefined,
  rewardId: undefined,
  quantity: undefined,
  weight: 0,
  redeemChannel: REDEEM_CHANNELS.length === 1 ? REDEEM_CHANNELS[0].value : undefined,
});

const SetupFormModal: React.FC<SetupFormModalProps> = ({
  mode,
  setup,
  isOpenModal,
  onClose,
  onSaveSuccess,
  campaignId,
}) => {
  const [form] = Form.useForm<SetupFormData>();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const isModal = mode === 'modal';

  const [prizeRows, setPrizeRows] = useState<PrizeRow[]>([]);
  const [draftRow, setDraftRow] = useState<PrizeRow>({ rewardTypeId: undefined, rewardId: undefined, quantity: undefined, weight: 0, redeemChannel: REDEEM_CHANNELS.length === 1 ? REDEEM_CHANNELS[0].value : undefined });
  const [prizesMap, setPrizesMap] = useState<Record<number, any[]>>({});
  const [prizeRowError, setPrizeRowError] = useState<string | null>(null);

  const [createSetup, { isLoading: isCreating }] = useCreateSetupMutation();
  const [updateSetup, { isLoading: isUpdating }] = useUpdateSetupMutation();

  const [getPrizesSimpleList] = useLazyGetPrizesSimpleListQuery();

  const [getPrizeTypesSimpleList] = useLazyGetPrizeTypesSimpleListQuery();
  const prizeTypes = useAppSelector(state => state.prizeTypesReducer).list.data || [];

  useEffect(() => {
    getInitialData();
  }, []);

  const getDefaultDraft = (types: any[]): PrizeRow => ({
    rewardTypeId: types.length === 1 ? types[0].id : undefined,
    rewardId: undefined,
    quantity: undefined,
    weight: 0,
    redeemChannel: REDEEM_CHANNELS.length === 1 ? REDEEM_CHANNELS[0].value : undefined,
  });

  useEffect(() => {
    if (prizeTypes.length === 1) {
      setDraftRow(prev => prev.rewardTypeId ? prev : { ...prev, rewardTypeId: prizeTypes[0].id });
      fetchPrizesByType(prizeTypes[0].id);
    }
  }, [prizeTypes]);

  useEffect(() => {
    if (!isOpenModal) return;

    if (setup) {
      form.setFieldsValue({
        name: setup.name || '',
        startAt: setup.startAt ? dayjs.utc(setup.startAt) : undefined,
        endAt: setup.endAt ? dayjs.utc(setup.endAt) : undefined,
        isActive: setup.isActive,
        isPublished: setup.isPublished,
      });
      if (setup.items && setup.items.length > 0) {
        const rows = setup.items.map(p => ({
          rewardTypeId: p.reward?.rewardType?.id || p.reward?.rewardTypeId || undefined,
          rewardId: p.reward?.id || p.rewardId,
          quantity: p.quantity,
          weight: p.weight,
          redeemChannel: p.redeemChannel || (REDEEM_CHANNELS.length === 1 ? REDEEM_CHANNELS[0].value : undefined),
        }));
        setPrizeRows(rows);
        setPrizesMap({});
        const typeIds = Array.from(new Set(rows.map(r => r.rewardTypeId).filter(Boolean))) as number[];
        typeIds.forEach(id => loadPrizesByType(id));
      } else {
        setPrizeRows(isModal ? [] : [DEFAULT_PRIZE_ROW(prizeTypes)]);
      }
      setDraftRow(getDefaultDraft(prizeTypes));
    } else {
      form.resetFields();
      setPrizesMap({});
      setPrizeRows(isModal ? [] : [DEFAULT_PRIZE_ROW(prizeTypes)]);
      setDraftRow(getDefaultDraft(prizeTypes));
      if (prizeTypes.length === 1) {
        loadPrizesByType(prizeTypes[0].id);
      }
    }
    setPrizeRowError(null);
  }, [isOpenModal, setup, form]);

  const getInitialData = async () => {
    try {
      const res = await getPrizeTypesSimpleList({ queryString: '' }).unwrap();
      dispatch(setPrizeTypesData({ list: res }));
    } catch (error) {
      console.error('Error fetching prize types list:', error);
    }
  };

  const fetchPrizesByType = async (rewardTypeId: number) => {
    if (prizesMap[rewardTypeId]) return;
    try {
      const campaignParam = campaignId ? `&campaignId=${campaignId}` : '';
      const res = await getPrizesSimpleList({ queryString: `rewardTypeId=${rewardTypeId}${campaignParam}` }).unwrap();
      setPrizesMap(prev => ({ ...prev, [rewardTypeId]: res.data || [] }));
    } catch (error) {
      console.error('Error fetching prizes by type:', error);
    }
  };

  const loadPrizesByType = async (rewardTypeId: number) => {
    try {
      const campaignParam = campaignId ? `&campaignId=${campaignId}` : '';
      const res = await getPrizesSimpleList({ queryString: `rewardTypeId=${rewardTypeId}${campaignParam}` }).unwrap();
      setPrizesMap(prev => ({ ...prev, [rewardTypeId]: res.data || [] }));
    } catch (error) {
      console.error('Error fetching prizes by type:', error);
    }
  };

  const getPrizesForType = (rewardTypeId: number | undefined) => {
    if (!rewardTypeId) return [];
    return prizesMap[rewardTypeId] || [];
  };

  const getMaxQuantityForPrize = (row: PrizeRow): number | undefined => {
    if (!row.rewardTypeId || !row.rewardId) return undefined;
    const prizes = getPrizesForType(row.rewardTypeId);
    const selectedPrize = prizes.find((p: any) => p.id === row.rewardId);
    return selectedPrize?.quantity;
  };

  // Fullscreen mode: add a new empty editable row
  const addPrizeRow = () => {
    const lastRow = prizeRows[prizeRows.length - 1];
    if (lastRow && (!lastRow.rewardTypeId || !lastRow.rewardId || !lastRow.quantity || lastRow.weight === undefined || lastRow.weight < 0 || !lastRow.redeemChannel)) {
      setPrizeRowError(intl.formatMessage({ id: 'Complete all fields of the current prize before adding a new one' }));
      return;
    }
    setPrizeRowError(null);
    setPrizeRows(prev => [...prev, DEFAULT_PRIZE_ROW(prizeTypes)]);
  };

  const confirmDraft = () => {
    if (!draftRow.rewardTypeId || !draftRow.rewardId || !draftRow.quantity || draftRow.weight === undefined || draftRow.weight < 0 || !draftRow.redeemChannel) {
      setPrizeRowError(intl.formatMessage({ id: 'Complete all fields of the current prize before adding a new one' }));
      return;
    }
    const usedIds = prizeRows.map(r => r.rewardId).filter(Boolean);
    if (usedIds.includes(draftRow.rewardId)) {
      setPrizeRowError(intl.formatMessage({ id: 'Duplicate prizes are not allowed' }));
      return;
    }
    setPrizeRows(prev => [...prev, { ...draftRow }]);
    setDraftRow(getDefaultDraft(prizeTypes));
    setPrizeRowError(null);
  };

  const removePrizeRow = (index: number) => {
    setPrizeRows(prev => prev.filter((_, i) => i !== index));
  };

  const updatePrizeRow = (index: number, field: keyof PrizeRow, value: any) => {
    const newRows = [...prizeRows];
    newRows[index] = { ...newRows[index], [field]: value };
    setPrizeRows(newRows);
    if (prizeRowError) setPrizeRowError(null);
  };

  const updateDraft = (field: keyof PrizeRow, value: any) => {
    setDraftRow(prev => ({ ...prev, [field]: value }));
    if (prizeRowError) setPrizeRowError(null);
  };

  const handleFinish = async (values: SetupFormData) => {
    if (prizeRows.length === 0) {
      setPrizeRowError(intl.formatMessage({ id: 'At least one prize is required' }));
      return;
    }

    const invalidQuantityPrize = prizeRows.find(p => {
      const maxQty = getMaxQuantityForPrize(p);
      return maxQty !== undefined && p.quantity !== undefined && p.quantity > maxQty;
    });
    if (invalidQuantityPrize) {
      const maxQty = getMaxQuantityForPrize(invalidQuantityPrize);
      setPrizeRowError(intl.formatMessage({ id: 'Prize quantity exceeds available amount' }, { max: maxQty }));
      return;
    }

    if (values.startAt && values.endAt && dayjs(values.endAt).isBefore(dayjs(values.startAt))) {
      toast.error({ title: intl.formatMessage({ id: 'End date must be after start date' }) });
      return;
    }

    const body: any = {
      name: values.name.trim(),
      startAt: values.startAt ? dayjs(values.startAt).format('YYYY-MM-DD') : null,
      endAt: values.endAt ? dayjs(values.endAt).format('YYYY-MM-DD') : null,
      isActive: values.isActive ?? true,
      isPublished: values.isPublished ?? false,
      ...(campaignId ? { campaignId } : {}),
      items: prizeRows.map(p => ({
        rewardId: p.rewardId,
        quantity: Number(p.quantity),
        weight: Number(p.weight),
        redeemChannel: p.redeemChannel,
      })),
    };

    try {
      if (setup && setup.id) {
        await updateSetup({ id: setup.id, data: body }).unwrap();
        toast.success({ title: intl.formatMessage({ id: 'Setup is updated successfully!' }) });
      } else {
        await createSetup(body).unwrap();
        toast.success({ title: intl.formatMessage({ id: 'Setup is created successfully!' }) });
      }

      onClose();
      onSaveSuccess();
      
    } catch (e: any) {
      console.error('Setup save error:', e);
      const defaultMsg = intl.formatMessage({ id: setup?.id ? 'Updating setup is failure!' : 'Creating setup is failure!' });
      toast.error({ title: getApiErrorMessage(e, intl.locale, defaultMsg) });
    }
  };

  const isEditMode = setup && setup.id;
  const isViewMode = !!(setup && (setup.isPublished || setup.hasRaffleConfig));

  const selectClassName = isModal ? 'select_modal' : 'select_form';
  const datePickerClassName = isModal ? 'date_picker_modal' : 'date_picker_form';

  const renderPrizeTypeSelect = (row: PrizeRow, index: number) => (
    <Select
      className={selectClassName}
      suffixIcon={<ArrowDropDown />}
      placeholder={intl.formatMessage({ id: 'Prize type' })}
      value={row.rewardTypeId}
      disabled={isViewMode}
      onChange={(value: number) => {
        const newRows = [...prizeRows];
        newRows[index] = { ...newRows[index], rewardTypeId: value, rewardId: undefined };
        setPrizeRows(newRows);
        fetchPrizesByType(value);
        if (prizeRowError) setPrizeRowError(null);
      }}
      dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
    >
      {prizeTypes.map((type: any) => (
        <Select.Option value={type.id} key={type.id}>
          {type.name}
        </Select.Option>
      ))}
    </Select>
  );

  const renderPrizeSelect = (row: PrizeRow, index: number) => {
    const usedRewardIds = prizeRows
      .filter((_, i) => i !== index)
      .map(r => r.rewardId)
      .filter(Boolean);
    const availablePrizes = getPrizesForType(row.rewardTypeId).filter(
      (prize: any) => !usedRewardIds.includes(prize.id)
    );

    return (
      <Select
        className={selectClassName}
        suffixIcon={<ArrowDropDown />}
        placeholder={intl.formatMessage({ id: 'Prizes' })}
        value={row.rewardId}
        disabled={isViewMode || !row.rewardTypeId}
        onChange={(value: number) => {
          const prizes = getPrizesForType(row.rewardTypeId);
          const selectedPrize = prizes.find((p: any) => p.id === value);
          const maxQty = selectedPrize?.quantity;
          const newRows = [...prizeRows];
          newRows[index] = {
            ...newRows[index],
            rewardId: value,
            quantity: (maxQty !== undefined && row.quantity !== undefined && row.quantity > maxQty) ? maxQty : row.quantity,
          };
          setPrizeRows(newRows);
          if (prizeRowError) setPrizeRowError(null);
        }}
        dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
      >
        {availablePrizes.map((prize: any) => (
          <Select.Option value={prize.id} key={prize.id}>
            {prize.name}
          </Select.Option>
        ))}
      </Select>
    );
  };

  const renderQuantityInput = (row: PrizeRow, index: number) => {
    const maxQuantity = getMaxQuantityForPrize(row);
    const exceedsMax = maxQuantity !== undefined && row.quantity !== undefined && row.quantity > maxQuantity;

    return (
      <div className="quantity_input_wrapper">
        <Input
          type="number"
          min={1}
          max={maxQuantity}
          placeholder={intl.formatMessage({ id: 'Prize quantity' })}
          value={row.quantity}
          disabled={isViewMode}
          onChange={e => {
            const val = e.target.value ? Number(e.target.value) : undefined;
            if (val !== undefined && maxQuantity !== undefined && val > maxQuantity) {
              updatePrizeRow(index, 'quantity', maxQuantity);
            } else {
              updatePrizeRow(index, 'quantity', val);
            }
          }}
          status={exceedsMax ? 'error' : undefined}
        />
        {maxQuantity !== undefined && (
          <span className="quantity_max_hint">
            <FormattedMessage id="Max" />: {maxQuantity}
          </span>
        )}
      </div>
    );
  };

  const renderWeightSlider = (row: PrizeRow, index: number) => (
    <div className="weight_slider_wrapper">
      <Slider
        min={0}
        max={100}
        value={row.weight ?? 0}
        disabled={isViewMode}
        onChange={(value: number) => updatePrizeRow(index, 'weight', value)}
      />
      <span className="weight_value">{row.weight ?? 0}</span>
    </div>
  );

  const renderRedeemChannelSelect = (row: PrizeRow, index: number) => (
    <Select
      className={selectClassName}
      suffixIcon={<ArrowDropDown />}
      placeholder={intl.formatMessage({ id: 'Redeem channel' })}
      value={row.redeemChannel}
      disabled={isViewMode}
      onChange={(value: string) => updatePrizeRow(index, 'redeemChannel', value)}
      dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
    >
      {REDEEM_CHANNELS.map(ch => (
        <Select.Option value={ch.value} key={ch.value}>
          {ch.label}
        </Select.Option>
      ))}
    </Select>
  );

  const renderRemoveButton = (index: number) => (
    <div className="prize_remove">
      {!isViewMode && prizeRows.length > 1 && (
        <Button className="remove_prize_btn" onClick={() => removePrizeRow(index)} type="default">
          <Delete />
        </Button>
      )}
    </div>
  );

  const getDraftPrizeName = (): string => {
    if (!draftRow.rewardTypeId || !draftRow.rewardId) return '';
    const prizes = getPrizesForType(draftRow.rewardTypeId);
    const prize = prizes.find((p: any) => p.id === draftRow.rewardId);
    return prize?.name || '';
  };

  const getConfirmedPrizeName = (row: PrizeRow): string => {
    if (!row.rewardTypeId || !row.rewardId) return '';
    const prizes = getPrizesForType(row.rewardTypeId);
    const prize = prizes.find((p: any) => p.id === row.rewardId);
    return prize?.name || `#${row.rewardId}`;
  };

  const renderPrizesSection = () => {
    if (isModal) {
      const usedRewardIds = prizeRows.map(r => r.rewardId).filter(Boolean);
      const availablePrizesForDraft = getPrizesForType(draftRow.rewardTypeId).filter(
        (prize: any) => !usedRewardIds.includes(prize.id)
      );
      const draftMaxQty = draftRow.rewardId
        ? getPrizesForType(draftRow.rewardTypeId).find((p: any) => p.id === draftRow.rewardId)?.quantity
        : undefined;
      const draftExceedsMax = draftMaxQty !== undefined && draftRow.quantity !== undefined && draftRow.quantity > draftMaxQty;

      return (
        <div className="prizes_two_col">
          {/* LEFT: entry form */}
          <div className="prizes_entry_col">
            <div className="prize_entry_field">
              <p className="field_label"><FormattedMessage id="Prize type" /></p>
              <Select
                className={selectClassName}
                suffixIcon={<ArrowDropDown />}
                placeholder={intl.formatMessage({ id: 'Prize type' })}
                value={draftRow.rewardTypeId}
                disabled={isViewMode}
                onChange={(value: number) => {
                  setDraftRow(prev => ({ ...prev, rewardTypeId: value, rewardId: undefined }));
                  fetchPrizesByType(value);
                  if (prizeRowError) setPrizeRowError(null);
                }}
                dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
              >
                {prizeTypes.map((type: any) => (
                  <Select.Option value={type.id} key={type.id}>{type.name}</Select.Option>
                ))}
              </Select>
            </div>

            <div className="prize_entry_field">
              <p className="field_label"><FormattedMessage id="Prizes" /></p>
              <Select
                className={selectClassName}
                suffixIcon={<ArrowDropDown />}
                placeholder={intl.formatMessage({ id: 'Prizes' })}
                value={draftRow.rewardId}
                disabled={isViewMode || !draftRow.rewardTypeId}
                onChange={(value: number) => {
                  const maxQty = getPrizesForType(draftRow.rewardTypeId).find((p: any) => p.id === value)?.quantity;
                  setDraftRow(prev => ({
                    ...prev,
                    rewardId: value,
                    quantity: (maxQty !== undefined && prev.quantity !== undefined && prev.quantity > maxQty) ? maxQty : prev.quantity,
                  }));
                  if (prizeRowError) setPrizeRowError(null);
                }}
                dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
              >
                {availablePrizesForDraft.map((prize: any) => (
                  <Select.Option value={prize.id} key={prize.id}>{prize.name}</Select.Option>
                ))}
              </Select>
            </div>

            <div className="prize_entry_row_inline">
              <div className="prize_entry_field">
                <p className="field_label"><FormattedMessage id="Prize quantity" /></p>
                <div className="quantity_input_wrapper">
                  <Input
                    type="number"
                    min={1}
                    max={draftMaxQty}
                    placeholder={intl.formatMessage({ id: 'Prize quantity' })}
                    value={draftRow.quantity}
                    disabled={isViewMode}
                    onChange={e => {
                      const val = e.target.value ? Number(e.target.value) : undefined;
                      updateDraft('quantity', (val !== undefined && draftMaxQty !== undefined && val > draftMaxQty) ? draftMaxQty : val);
                    }}
                    status={draftExceedsMax ? 'error' : undefined}
                  />
                  {draftMaxQty !== undefined && (
                    <span className="quantity_max_hint"><FormattedMessage id="Max" />: {draftMaxQty}</span>
                  )}
                </div>
              </div>
              <div className="prize_entry_field">
                <p className="field_label"><FormattedMessage id="Redeem channel" /></p>
                <Select
                  className={selectClassName}
                  suffixIcon={<ArrowDropDown />}
                  placeholder={intl.formatMessage({ id: 'Redeem channel' })}
                  value={draftRow.redeemChannel}
                  disabled={isViewMode}
                  onChange={(value: string) => updateDraft('redeemChannel', value)}
                  dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
                >
                  {REDEEM_CHANNELS.map(ch => (
                    <Select.Option value={ch.value} key={ch.value}>{ch.label}</Select.Option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="prize_entry_field">
              <p className="field_label"><FormattedMessage id="Prize weight" /> <span className="weight_badge">{draftRow.weight ?? 0}</span></p>
              <div className="weight_slider_wrapper">
                <Slider
                  min={0}
                  max={100}
                  value={draftRow.weight ?? 0}
                  disabled={isViewMode}
                  onChange={(value: number) => updateDraft('weight', value)}
                />
              </div>
            </div>

            {prizeRowError && <p className="prize_row_error">{prizeRowError}</p>}

            {!isViewMode && (
              <Button className="add_prize_btn" onClick={confirmDraft} type="default">
                <Add /> <FormattedMessage id="Add prize" />
              </Button>
            )}
          </div>

          {/* RIGHT: confirmed prizes list */}
          <div className="prizes_list_col">
            <p className="prizes_list_label">
              <FormattedMessage id="Added prizes" /> <span className="prizes_count">{prizeRows.length}</span>
            </p>
            {prizeRows.length === 0 ? (
              <div className="prizes_empty">
                <EmojiEvents />
                <span><FormattedMessage id="No prizes added yet" /></span>
              </div>
            ) : (
              <div className="prizes_list_scroll">
                {prizeRows.map((row, index) => (
                  <div className="prize_chip" key={index}>
                    <div className="prize_chip_info">
                      <span className="prize_chip_name">{getConfirmedPrizeName(row)}</span>
                      <span className="prize_chip_meta">x{row.quantity} · w{row.weight}</span>
                    </div>
                    {!isViewMode && (
                      <button className="prize_chip_remove" onClick={() => removePrizeRow(index)} type="button">
                        <Delete fontSize="inherit" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="setup_form_row prizes_section">
        <div className="prizes_header">
          <p className="title"><FormattedMessage id="Prizes" /></p>
        </div>
        {prizeRows.map((row, index) => (
          <div className="prize_row" key={index}>
            <div className="prize_row_top">
              <div className="prize_type_select">
                <p className="field_label"><FormattedMessage id="Prize type" /></p>
                {renderPrizeTypeSelect(row, index)}
              </div>
              <div className="prize_select">
                <p className="field_label"><FormattedMessage id="Prizes" /></p>
                {renderPrizeSelect(row, index)}
              </div>
            </div>
            <div className="prize_row_bottom">
              <div className="prize_bottom_left">
                <div className="prize_redeem_channel">
                  <p className="field_label"><FormattedMessage id="Redeem channel" /></p>
                  {renderRedeemChannelSelect(row, index)}
                </div>
                <div className="prize_quantity">
                  <p className="field_label"><FormattedMessage id="Prize quantity" /></p>
                  {renderQuantityInput(row, index)}
                </div>
              </div>
              <div className="prize_bottom_right">
                <div className="prize_weight">
                  <p className="field_label"><FormattedMessage id="Prize weight" /></p>
                  {renderWeightSlider(row, index)}
                </div>
                {renderRemoveButton(index)}
              </div>
            </div>
          </div>
        ))}
        {prizeRowError && <p className="prize_row_error">{prizeRowError}</p>}
        {!isViewMode && (
          <Button className="add_prize_btn" onClick={addPrizeRow} type="default">
            <Add /> <FormattedMessage id="Add prize" />
          </Button>
        )}
      </div>
    );
  };

  const renderFormContent = () => (
    <Form
      form={form}
      className="setup_form"
      onFinish={handleFinish}
      initialValues={{ name: null, startAt: undefined, endAt: undefined, isActive: true, isPublished: false }}
    >
      <>
        <div className="setup_form_fields">
            <div className="setup_form_row">
              <div className="setup_field">
                <p className="title"><FormattedMessage id="Setup name" /></p>
                <Form.Item name="name" rules={[{ required: !isViewMode, message: intl.formatMessage({ id: 'Name is required' }) }, sanitizeRule(intl.formatMessage({ id: 'Invalid characters detected' }))]}>
                  <Input placeholder={intl.formatMessage({ id: 'Setup name' })} maxLength={200} disabled={isViewMode} />
                </Form.Item>
              </div>
            </div>
            <div className="setup_form_row two_columns">
              <div className="setup_field">
                <p className="title"><FormattedMessage id="Start date" /></p>
                <Form.Item name="startAt" rules={[{ required: !isViewMode, message: intl.formatMessage({ id: 'Field is required' }) }]}>
                  <DatePicker className={datePickerClassName} format="DD/MM/YYYY" placeholder={intl.formatMessage({ id: 'Select date' })} style={{ width: '100%' }} disabled={isViewMode} />
                </Form.Item>
              </div>
              <div className="setup_field">
                <p className="title"><FormattedMessage id="End date" /></p>
                <Form.Item name="endAt" rules={[{ required: !isViewMode, message: intl.formatMessage({ id: 'Field is required' }) }]}>
                  <DatePicker className={datePickerClassName} format="DD/MM/YYYY" placeholder={intl.formatMessage({ id: 'Select date' })} style={{ width: '100%' }} disabled={isViewMode} />
                </Form.Item>
              </div>
            </div>
            {isEditMode && (
              <div className="setup_form_row">
                <div className="setup_field status_field">
                  <p className="title"><FormattedMessage id="Active" /></p>
                  <Form.Item name="isActive" valuePropName="checked" noStyle>
                    <Switch disabled={isViewMode} />
                  </Form.Item>
                </div>
              </div>
            )}
            {renderPrizesSection()}
          </div>
          <div className="setup_form_actions">
            {isViewMode ? (
              <div className="view_mode_hint">
                <p>
                  <FormattedMessage
                    id={setup?.hasRaffleConfig ? 'Cannot edit setup with raffle config' : 'Unpublish to edit this setup'}
                  />
                </p>
              </div>
            ) : (
              <div className="aditional_detail">
                <p><FormattedMessage id="Published" /></p>
                <Form.Item name="isPublished" valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
              </div>
            )}
            <div className="btn_wrapper">
              {isViewMode ? (
                <Button className="cancel_btn" onClick={onClose}>
                  <FormattedMessage id="Close" />
                </Button>
              ) : (
                <>
                  <Button className="save_btn" htmlType="submit" loading={isCreating || isUpdating}>
                    <FormattedMessage id={isEditMode ? 'Save changes' : 'Save'} />
                    <ArrowForward />
                  </Button>
                  <Button className="cancel_btn" onClick={onClose}>
                    <FormattedMessage id="Cancel" />
                  </Button>
                </>
              )}
            </div>
          </div>
      </>
    </Form>
  );

  if (isModal) {
    return (
      <ModalDlg
        className="setup_view_modal"
        visible={isOpenModal ?? false}
        width={820}
        handleCancel={onClose}
        isCenter
      >
        <Form
          form={form}
          onFinish={handleFinish}
          initialValues={{ name: null, startAt: undefined, endAt: undefined, isActive: true, isPublished: false }}
        >
          {/* ── Dark header ───────────────────────────────────────────────── */}
          <div className="svm_header">
            <div className="svm_header_left">
              <Settings fontSize="small" />
              <span><FormattedMessage id={isViewMode ? 'View setup' : isEditMode ? 'Edit setup' : 'New setup'} /></span>
            </div>
            <button className="svm_close" onClick={onClose} type="button">
              <Close fontSize="small" />
            </button>
          </div>

          {/* ── Two-column body ───────────────────────────────────────────── */}
          <div className="svm_body">

            {/* Column A — config + prizes entry form */}
            <div className="svm_col_a">
              <p className="svm_section_label"><Settings fontSize="inherit" /><FormattedMessage id="Setup information" /></p>

              <div className="svm_field">
                <p className="svm_label"><FormattedMessage id="Setup name" /></p>
                <Form.Item name="name" rules={[{ required: !isViewMode, message: intl.formatMessage({ id: 'Name is required' }) }, sanitizeRule(intl.formatMessage({ id: 'Invalid characters detected' }))]}>
                  <Input placeholder={intl.formatMessage({ id: 'Setup name' })} maxLength={200} disabled={isViewMode} />
                </Form.Item>
              </div>

              <div className="svm_row_2col">
                <div className="svm_field">
                  <p className="svm_label"><FormattedMessage id="Start date" /></p>
                  <Form.Item name="startAt" rules={[{ required: !isViewMode, message: intl.formatMessage({ id: 'Field is required' }) }]}>
                    <DatePicker className={datePickerClassName} format="DD/MM/YYYY" placeholder={intl.formatMessage({ id: 'Select date' })} style={{ width: '100%' }} disabled={isViewMode} />
                  </Form.Item>
                </div>
                <div className="svm_field">
                  <p className="svm_label"><FormattedMessage id="End date" /></p>
                  <Form.Item name="endAt" rules={[{ required: !isViewMode, message: intl.formatMessage({ id: 'Field is required' }) }]}>
                    <DatePicker className={datePickerClassName} format="DD/MM/YYYY" placeholder={intl.formatMessage({ id: 'Select date' })} style={{ width: '100%' }} disabled={isViewMode} />
                  </Form.Item>
                </div>
              </div>

              <div className="svm_divider" />

              <p className="svm_section_label"><EmojiEvents fontSize="inherit" /><FormattedMessage id="Prizes" /></p>

              {/* Draft entry form */}
              {!isViewMode && (
                <>
                  <div className="svm_field">
                    <p className="svm_label"><FormattedMessage id="Prize type" /></p>
                    <Select
                      className={selectClassName}
                      suffixIcon={<ArrowDropDown />}
                      placeholder={intl.formatMessage({ id: 'Prize type' })}
                      value={draftRow.rewardTypeId}
                      onChange={(value: number) => {
                        setDraftRow(prev => ({ ...prev, rewardTypeId: value, rewardId: undefined }));
                        fetchPrizesByType(value);
                        if (prizeRowError) setPrizeRowError(null);
                      }}
                      dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
                      style={{ width: '100%' }}
                    >
                      {prizeTypes.map((type: any) => (
                        <Select.Option value={type.id} key={type.id}>{type.name}</Select.Option>
                      ))}
                    </Select>
                  </div>

                  <div className="svm_field">
                    <p className="svm_label"><FormattedMessage id="Prizes" /></p>
                    <Select
                      className={selectClassName}
                      suffixIcon={<ArrowDropDown />}
                      placeholder={intl.formatMessage({ id: 'Prizes' })}
                      value={draftRow.rewardId}
                      disabled={!draftRow.rewardTypeId}
                      onChange={(value: number) => {
                        const maxQty = getPrizesForType(draftRow.rewardTypeId).find((p: any) => p.id === value)?.quantity;
                        setDraftRow(prev => ({
                          ...prev,
                          rewardId: value,
                          quantity: (maxQty !== undefined && prev.quantity !== undefined && prev.quantity > maxQty) ? maxQty : prev.quantity,
                        }));
                        if (prizeRowError) setPrizeRowError(null);
                      }}
                      dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
                      style={{ width: '100%' }}
                    >
                      {getPrizesForType(draftRow.rewardTypeId)
                        .filter((p: any) => !prizeRows.map(r => r.rewardId).includes(p.id))
                        .map((prize: any) => (
                          <Select.Option value={prize.id} key={prize.id}>{prize.name}</Select.Option>
                        ))}
                    </Select>
                  </div>

                  <div className="svm_row_2col">
                    <div className="svm_field">
                      <p className="svm_label"><FormattedMessage id="Prize quantity" /></p>
                      <div className="quantity_input_wrapper">
                        <Input
                          type="number"
                          min={1}
                          placeholder={intl.formatMessage({ id: 'Prize quantity' })}
                          value={draftRow.quantity}
                          onChange={e => {
                            const val = e.target.value ? Number(e.target.value) : undefined;
                            const maxQty = draftRow.rewardId
                              ? getPrizesForType(draftRow.rewardTypeId).find((p: any) => p.id === draftRow.rewardId)?.quantity
                              : undefined;
                            updateDraft('quantity', (val !== undefined && maxQty !== undefined && val > maxQty) ? maxQty : val);
                          }}
                        />
                        {draftRow.rewardId && (() => {
                          const maxQty = getPrizesForType(draftRow.rewardTypeId).find((p: any) => p.id === draftRow.rewardId)?.quantity;
                          return maxQty !== undefined ? <span className="quantity_max_hint"><FormattedMessage id="Max" />: {maxQty}</span> : null;
                        })()}
                      </div>
                    </div>
                    <div className="svm_field">
                      <p className="svm_label"><FormattedMessage id="Redeem channel" /></p>
                      <Select
                        className={selectClassName}
                        suffixIcon={<ArrowDropDown />}
                        placeholder={intl.formatMessage({ id: 'Redeem channel' })}
                        value={draftRow.redeemChannel}
                        onChange={(value: string) => updateDraft('redeemChannel', value)}
                        dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
                        style={{ width: '100%' }}
                      >
                        {REDEEM_CHANNELS.map(ch => (
                          <Select.Option value={ch.value} key={ch.value}>{ch.label}</Select.Option>
                        ))}
                      </Select>
                    </div>
                  </div>

                  <div className="svm_field">
                    <p className="svm_label">
                      <FormattedMessage id="Prize weight" />
                      <span className="svm_weight_badge">{draftRow.weight ?? 0}</span>
                    </p>
                    <Slider
                      min={0} max={100}
                      value={draftRow.weight ?? 0}
                      onChange={(v: number) => updateDraft('weight', v)}
                    />
                  </div>

                  {prizeRowError && <p className="svm_error">{prizeRowError}</p>}

                  <Button className="svm_add_btn" onClick={confirmDraft} type="default">
                    <Add /><FormattedMessage id="Add prize" />
                  </Button>
                </>
              )}

              {isViewMode && prizeRows.length === 0 && (
                <p className="svm_view_hint"><FormattedMessage id="No prizes added yet" /></p>
              )}
            </div>

            {/* Column B — confirmed prizes list */}
            <div className="svm_col_b">
              <div className="svm_col_b_header">
                <span className="svm_col_b_title"><FormattedMessage id="Added prizes" /></span>
                <span className="svm_col_b_count">{prizeRows.length}</span>
              </div>

              {prizeRows.length === 0 ? (
                <div className="svm_empty">
                  <EmojiEvents />
                  <span><FormattedMessage id="No prizes added yet" /></span>
                </div>
              ) : (
                <div className="svm_prizes_list">
                  {prizeRows.map((row, index) => (
                    <div className="svm_prize_card" key={index}>
                      <div className="svm_prize_card_info">
                        <span className="svm_prize_card_name">{getConfirmedPrizeName(row)}</span>
                        <span className="svm_prize_card_meta">x{row.quantity} · w{row.weight} · {row.redeemChannel}</span>
                      </div>
                      {!isViewMode && (
                        <button className="svm_prize_card_remove" onClick={() => removePrizeRow(index)} type="button">
                          <Delete fontSize="inherit" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Footer ───────────────────────────────────────────────────── */}
          <div className="svm_footer">
            {isViewMode ? (
              <p className="svm_view_hint svm_footer_hint">
                <FormattedMessage id={setup?.hasRaffleConfig ? 'Cannot edit setup with raffle config' : 'Unpublish to edit this setup'} />
              </p>
            ) : (
              <div className="svm_footer_publish">
                <p><FormattedMessage id="Published" /></p>
                <Form.Item name="isPublished" valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
              </div>
            )}
            <div className="svm_footer_actions">
              {isViewMode ? (
                <Button className="svm_cancel_btn" onClick={onClose}>
                  <FormattedMessage id="Close" />
                </Button>
              ) : (
                <>
                  <Button className="svm_cancel_btn" onClick={onClose}>
                    <FormattedMessage id="Cancel" />
                  </Button>
                  <Button className="svm_save_btn" htmlType="submit" loading={isCreating || isUpdating}>
                    <FormattedMessage id={isEditMode ? 'Save changes' : 'Save'} />
                    <ArrowForward />
                  </Button>
                </>
              )}
            </div>
          </div>
        </Form>
      </ModalDlg>
    );
  }

  if (!isOpenModal) return null;

  return (
    <div className="setup_form_fullscreen">
      <div className="setup_form_wrapper">
        <div className="setup_form_header">
          <h3><FormattedMessage id={isViewMode ? 'View setup' : isEditMode ? 'Edit setup' : 'New setup'} /></h3>
        </div>
        {renderFormContent()}
      </div>
    </div>
  );
};

export default SetupFormModal;
